import { useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import useShowPDF from "@util/hook/useShowPDF";
import useValidationErrors from "@util/hook/useValidationErrors";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER } from "@icoach/router/routerPath";
import { isEmpty } from "@util/utils";

/**
 * 循環訂單提交 Hook
 * 負責處理循環訂單的提交邏輯
 *
 * @param {Object} options - 設定選項
 * @param {Function} [options.onSuccess] - 成功後的回調（可選，會覆蓋預設行為）
 * @returns {Object} 提交函數和狀態
 */
const useCyclePurchaseOrderSubmit = ({ onSuccess } = {}) => {
	const history = useHistory();
	const showPDF = useShowPDF();
	const { postCyclePurchaseOrderApi } = useCyclePurchaseApi();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const apiSendingRef = useRef(false);

	/**
	 * 提交循環訂單
	 * @param {Object} params - 提交參數
	 * @returns {Promise<boolean>} 是否成功
	 */
	const submit = useCallback(
		async params => {
			if (apiSendingRef.current) return false;

			clearValidationErrors();
			apiSendingRef.current = true;

			try {
				const resp = await postCyclePurchaseOrderApi(params);
				const { isError = false, result, contractID, completedPath } = resp || {};
				const { memberID } = params || {};

				if (isError) {
					// 0002 錯誤
					updateValidationErrors(result);
					apiSendingRef.current = false;
					return false;
				} else if (isEmpty(resp)) {
					// 失敗
					apiSendingRef.current = false;
					return false;
				} else {
					// 成功
					if (onSuccess && typeof onSuccess === "function") {
						// 自訂成功處理
						onSuccess(resp, params);
					} else {
						// 預設成功處理
						// 開啟PDF
						if (completedPath) {
							showPDF({
								open: true,
								pdfUrl: completedPath,
							});
						}
						// 轉跳訂單資料頁 唯讀
						window.setTimeout(() => {
							history.push(DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_MAIN_PATH(memberID, contractID));
						}, 10);
					}

					apiSendingRef.current = false;
					return true;
				}
			} catch (error) {
				console.error("提交循環訂單失敗:", error);
				apiSendingRef.current = false;
				return false;
			}
		},
		// eslint-disable-next-line
		[postCyclePurchaseOrderApi, updateValidationErrors, clearValidationErrors, onSuccess, showPDF, history],
	);

	/**
	 * 檢查是否正在提交中
	 */
	const isSubmitting = useCallback(
		() => {
			return apiSendingRef.current;
		},
		// eslint-disable-next-line
		[],
	);

	return {
		submit,
		isSubmitting,
		validationErrors,
		clearValidationErrors,
	};
};

export default useCyclePurchaseOrderSubmit;
